import { QueryRef } from 'apollo-angular'
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { KeywordMutations } from '../../../graphql/mutations/keyword.mutations'
import { Subscription } from 'rxjs'
import get from 'lodash-es/get'
import { MatDialog } from '@angular/material/dialog'
import { SaveKeywordDialog } from '../save-keyword-dialog.component'
import { UserMutations } from '../../../graphql/mutations/user.mutations'
import { sweetAlert } from '../../../bmAlert'
import { CustomerActions } from '../../../actions/customer-actions'
import { KeywordFormComponent } from '../keyword-form/keyword-form.component'
import { MultipassKeywordFormComponent } from '../keyword-form/multipass-keyword-form.component'
import { KeywordQueries, IAccount, IContentType, ICustomer, Request, NewKeywordPageData, RequestActivationMethod, IPriceList } from '../../../graphql/queries/keyword.queries'
import { PaymentOption, KeywordFormPaymentOptionType } from 'interfaces/post-credit-payment.interface'
import { allowedContentTypes, updateCustomersNavRequestsCount } from '../utils'
import { RequestMutations } from 'graphql/mutations/request.mutations'
import { VideoFormComponent } from 'modules/keywords/video-form/video-form.component'
import { isVideoPlanCode } from 'src/utils'
import { Document } from 'types'
import { stateGroupForKeyword } from 'src/logic/keyword-states'
import { DEFAULT_DIALOG_OPTIONS } from 'src/modules/shared/constants/default-dialog-options'

@Component({
  template: `
    <div class="row">
      <div class="col-md-12">
        <h5 class="ml-3">New <span *ngIf="isVideoCustomer">video</span>  Request</h5>
      </div>
    </div>
    <div *ngIf="customer && !isVideoCustomer">
      <multipass-keyword-form
        *ngIf="customer?.multipass"
        class="small-horizontal-buffer bottom-buffer"
        [account]="account"
        [customer]="customer"
        [duplicateId]="dupId"
        [allContentTypes]="allContentTypes"
        [allowedContentTypes]="allowedContentTypes"
        [priceList]="priceList"
        [submitting]="submitting"
        [contentTypeId]="contentTypeId"
        (create)="onKeywordFormCreate($event)"
        (duplicate)="onKeywordFormDuplicate($event)"
        (cancel)="onCancel()"
        #multipasskeywordform>
      </multipass-keyword-form>
      <keyword-form
        *ngIf="customer && !customer.multipass"
        class="small-horizontal-buffer bottom-buffer"
        [account]="account"
        [customer]="customer"
        [duplicateId]="dupId"
        [allContentTypes]="allContentTypes"
        [allowedContentTypes]="allowedContentTypes"
        [submitting]="submitting"
        [contentTypeId]="contentTypeId"
        (create)="onKeywordFormCreate($event)"
        (duplicate)="onKeywordFormDuplicate($event)"
        (cancel)="onCancel()"
        #keywordform>
      </keyword-form>
    </div>
    <div *ngIf="isVideoCustomer">
      <video-form
        class="small-horizontal-buffer bottom-buffer"
        [customer]="customer"
        [duplicateId]="dupId"
        (cancel)="onCancel()"
        (save)="createVideoRequest($event)"
        (search)="searchContent($event)"
      ></video-form>
    </div>
  `,
  selector: 'new-keyword-page'
})
export class NewKeywordPage implements OnInit, OnDestroy {
  queryRef: QueryRef<NewKeywordPageData>;
  routeObs: Subscription;
  dialogSub: Subscription;
  querySub: Subscription;

  customerId: number = null;
  account: IAccount = null;
  customer: ICustomer = null;
  reminderBetterSubmissions: boolean = true;
  allContentTypes: IContentType[];
  allowedContentTypes: IContentType[];
  priceList: IPriceList;
  contentTypeId: number;
  submitting: boolean = false;

  dupId: number = null;

  @ViewChild(KeywordFormComponent) public keywordForm: KeywordFormComponent;
  @ViewChild(MultipassKeywordFormComponent) public multipassKeywordForm: MultipassKeywordFormComponent;
  @ViewChild(VideoFormComponent) public videoForm: VideoFormComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private keywordMutations: KeywordMutations,
    private userMutations: UserMutations,
    private requestMutations: RequestMutations,
    private dialog: MatDialog,
    private customerActions: CustomerActions,
    private keywordQueries: KeywordQueries,
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(contentTypeParams => {
      this.contentTypeId = +contentTypeParams['contentTypeId']
    })
    this.route.queryParams.subscribe(queryParams => {
      this.dupId = +queryParams.dupId
    })
    this.routeObs = this.route.parent.parent.params.subscribe(params => {
      this.customerId = +params['customerId']

      if (this.customerId) {
        this.queryRef = this.keywordQueries.newKeywordPageQuery(this.customerId, '', true)
        this.querySub = this.queryRef.valueChanges.subscribe(({ data }) => {
          if (data) {
            const firstLoad = !this.customer && data.customer
            this.customer = data.customer
            this.account = data.account
            this.reminderBetterSubmissions = data.currentUser.uiPreferences.reminderBetterSubmissions
            this.allContentTypes = data.contentTypes
            this.allowedContentTypes = this.customer.multipass ? this.allContentTypes : allowedContentTypes(this.allContentTypes, get(this.customer, 'plan.words'))
            this.priceList = data.customer.subscriptionPriceList
            // When brought here after successfully connecting to hubspot
            const queryParams = this.route.parent.parent.snapshot.queryParams
            const hubspotConnectedAlert = queryParams['modal_success'] === 'hubspot_connected'
            if (firstLoad && hubspotConnectedAlert && this.isVideoCustomer && this.customer.hubspotCredential) {
              const title = "Success! Your HubSpot account is connected."
              const text = "Get a video by creating a video request. Our team can't get started without your direction."
              sweetAlert.success({title, text, confirmButtonText: 'Create a video request'}).then(() => {
                // Don't show modal again after dismissing
                this.router.navigate(['./'], { relativeTo: this.route })
              })
            }
          }
        })
      }
    })
  }

  get isVideoCustomer(): boolean {
    return this.customer?.multipass ? false : isVideoPlanCode(this.customer?.plan?.code)
  }

  openDialog(createKeywordObj: { body: object, files: File[], selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType>}): void {
    const dialogRef = this.dialog.open(SaveKeywordDialog, {
      ...DEFAULT_DIALOG_OPTIONS,
      data: {
        newRequest: true,
        dontShowAgain: !this.reminderBetterSubmissions,
        contentTypeId: this.contentTypeId,
        message: createKeywordObj.selectedPaymentOption.confirmMessage,
        buttonText: 'Activate'
      }
    })

    this.dialogSub = dialogRef.afterClosed().subscribe(closedFromButton => {
      if (closedFromButton && (closedFromButton.submit || closedFromButton.dontShowAgain)) {
        this.reminderBetterSubmissions = !closedFromButton.dontShowAgain
        if (!this.reminderBetterSubmissions) {
          this.userMutations.updateUserPreferences({ reminderBetterSubmissions: this.reminderBetterSubmissions })
        }
        if (closedFromButton.submit) this.createKeyword(createKeywordObj)
      } else {
        this.submitting = false
      }
    })
  }

  onKeywordFormDuplicate(keywordDuplicateObj: { body: object, documents: Document[], files: File[] }): void {
    this.duplicateKeyword(keywordDuplicateObj)
  }

  duplicateKeyword({ body, documents, files }: { body: object, documents: Document[], files: File[] }): void {
    const inputObj = {
      customerId: this.customerId,
      body,
      documents,
      files
    }
    this.keywordMutations.createKeyword(inputObj, this.customer.multipass).then(data => {
      this.navigateToRequests(body['state'])
      if (data) updateCustomersNavRequestsCount(data, this.customerActions)
    }).catch(() => {
      this.submitting = false
    })
  }

  onKeywordFormCreate(keywordCreateObj: { body: object, files: File[], selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType> }): void {
    this.submitting = true
    const state = <string>keywordCreateObj.body['state']

    if (['active', 'assigned'].includes(state) && this.reminderBetterSubmissions) {
      this.openDialog(keywordCreateObj)
    } else {
      this.createKeyword(keywordCreateObj)
    }
  }

  createKeyword({ body, files }: { body: object, files: File[] }): void {
    const inputObj = {
      customerId: this.customerId,
      body,
      documents: [],
      files
    }
    this.keywordMutations.createKeyword(inputObj, this.customer.multipass)
    .then(data => {
      this.navigateToRequests(body['state'])
      // remove when customer nav uses graphql
      if (data) updateCustomersNavRequestsCount(data, this.customerActions)
    })
    .catch(() => {
      this.submitting = false
    })
  }

  onCancel(): void {
    this.router.navigate(['../../'], { relativeTo: this.route })
  }

  ngOnDestroy(): void {
    if (this.routeObs) this.routeObs.unsubscribe()
    if (this.dialogSub) this.dialogSub.unsubscribe()
    if (this.querySub) this.querySub.unsubscribe()
  }

  openDialogForVideo({ request }: { request: Request }): void {
    let dialogRef = this.dialog.open(SaveKeywordDialog, {
      ...DEFAULT_DIALOG_OPTIONS,
      data: { dontShowAgain: !this.reminderBetterSubmissions, contentTypeId: 99 }
    })

    this.dialogSub = dialogRef.afterClosed().subscribe(closedFromButton => {
      if (closedFromButton && (closedFromButton.submit || closedFromButton.dontShowAgain)) {
        this.reminderBetterSubmissions = !closedFromButton.dontShowAgain
        if (!this.reminderBetterSubmissions) {
          this.userMutations.updateUserPreferences({ reminderBetterSubmissions: this.reminderBetterSubmissions })
        }
        if (closedFromButton.submit) this.createVideoRequest({ request })
      }
      this.videoForm.submitting = false
    })
  }

  createVideoRequest({ request, activationMethod }: { request: Request, activationMethod?: RequestActivationMethod }): void {
    let { id, ...sourceContentData } = request.sourceContent
    const inputObj = {
      customerId: this.customerId,
      request: { ...request, sourceContent: sourceContentData, type: 'VideoRequest' as 'VideoRequest' },
      activationMethod
    }
    this.requestMutations.createOrUpdateRequest(inputObj).then(data => {
      const state = data.request.state
      let title
      let text
      let buttonText

      if (state === 'active') {
        title = "You've activated a new request!"
        text = "Sit tight, our team is crafting your Verblio Vid. You can expect a draft to review within 72 hours."
        buttonText = "Great!"
      }

      else if (state === 'draft') {
        title = "You've saved a draft."
        text = "But remember, our team won't see your request until you come back and activate it."
        buttonText = "Okay"
      }

      this.videoForm.form.reset()
      this.videoForm.submitting = false
      this.router.navigate(['../../'], { relativeTo: this.route, queryParams: { state: state } })
      sweetAlert.success({title, text, confirmButtonText: buttonText})
      if (data) updateCustomersNavRequestsCount(data, this.customerActions)
    }).catch(error => {
      this.videoForm.submitting = false
    })
  }

  navigateToRequests(state: string): void {
    let title
    let text
    let buttonText

    if (state === 'active') {
      title = "You've activated a new request!"
      text = "Sit tight, our writers are crafting submissions for you to review"
      buttonText = "Great!"
    }

    else if (state === 'paused') {
      title = "You've saved a draft."
      text = "But remember, writers won't see your request until you come back and activate it."
      buttonText = "Okay"
    }

    else if (state === 'assigned') {
      title = "You've assigned a new request!"
      text = "Sit tight, the writer has 24 hours to respond"
      buttonText = "Great!"
    }

    const kf = this.keywordForm || this.multipassKeywordForm
    kf.form.reset()
    this.router.navigate(['../../'], { relativeTo: this.route, queryParams: { group: stateGroupForKeyword({ state }), order: 'updatedAt DESC!' } })
    sweetAlert.success({title, text, confirmButtonText: buttonText})
  }

  searchContent(query: string): void {
    this.queryRef.refetch({
      customerId: this.customerId,
      contentSearch: query
    })
  }


}
